import { Item } from "@volca/ui/components/ui/item";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import { packChatPreview } from "@/payroll/pack-chat/message-preview";
import { PackChatStatusMark } from "@/payroll/pack-chat/pack-chat-status-mark";
import type { IPackChatTechnicianGroup } from "@/payroll/pack-chat/types";

const technicianSummary = (group: IPackChatTechnicianGroup) => {
  const total = group.threads.length;
  const plural = total === 1 ? "" : "s";
  const count =
    group.openCount === 0
      ? `${total} thread${plural}`
      : group.openCount === total
        ? `${total} open`
        : `${total} threads · ${group.openCount} open`;

  return `${count} · ${dayjs(group.lastUpdatedAt).fromNow()}`;
};

export const PackChatTechnicianRow = ({
  group,
  selected,
  onSelect,
}: {
  group: IPackChatTechnicianGroup;
  selected: boolean;
  onSelect: () => void;
}) => {
  const open = group.openCount > 0;
  const last = group.lastMessageThread?.last_message;
  const preview = last ? packChatPreview(last) : null;

  return (
    <Item
      render={<button type="button" />}
      onClick={onSelect}
      aria-pressed={selected}
      className={cn("rounded-none text-left", {
        "bg-selected text-brand hover:bg-selected-hovered": selected,
      })}
    >
      <Stack
        className="w-full"
        direction="row"
        align="start"
        justify="between"
        gap={2}
      >
        <Stack gap={0} align="stretch" className="min-w-0">
          <Text
            variant="label"
            className="truncate"
            title={group.technician.name}
          >
            {group.technician.name}
          </Text>
          <Text
            variant="caption"
            tone="muted"
            className="truncate"
            title={technicianSummary(group)}
          >
            {technicianSummary(group)}
          </Text>
          {last && (
            <Text
              variant="body"
              tone="muted"
              title={
                preview
                  ? preview.author
                    ? `${preview.author}: ${preview.body}`
                    : preview.body
                  : undefined
              }
              className="truncate pt-0.5"
            >
              {preview &&
                (preview.author
                  ? `${preview.author}: ${preview.body}`
                  : preview.body)}
            </Text>
          )}
        </Stack>
        <PackChatStatusMark
          focusable={false}
          hasAiMessages={group.hasAiMessages}
          needsManager={group.needsManager}
          responseOwner={group.responseOwner}
          open={open}
          label={open ? `${group.openCount} open` : "All closed"}
          className="mt-0.5"
        />
      </Stack>
    </Item>
  );
};
